/**
 * THE SCATTER, AS NODES — today's trees, rocks and buildings under the node contract, unchanged.
 *
 * This is the adapter that lets the existing system live under the new one without becoming it. The
 * scatter's enumeration is CHUNK-LOCAL (its exclusion rules compare against what was already placed
 * in the same fine chunk, so a coarse footprint has to be walked as fine chunks — see
 * StampPointGenerator and MAX_SCATTER_LOD), which is not how a node tree wants to enumerate anything.
 * Rather than pretend otherwise, the `scatter` node IS that chunk footprint: one node per request,
 * whose children are the stamps the existing pass produces. Its own geometry is nothing.
 *
 * What the walk adds even here, and what makes it worth doing before any new content depends on it:
 * every stamp arrives with a declared AABB, so pruning, the vertical clip and the draw all read the
 * same box (`stampFineBounds`) instead of three near-copies of the same arithmetic.
 *
 * CHILD ORDER IS THE BUILDINGS-LAST ORDER. Stamp voxels are written last-wins, and a building's
 * interior carve has to remove the foliage that was scattered into it. `applyStamps` owned that sort
 * when it owned the list; the list is the walk's now, so the sort is here.
 */
import { MAX_SCATTER_LOD, isBuildingStamp, maxStampLod, stampFineBounds } from '../../stamps/index.js';
import { hashInt2 } from '../../../util/rng.js';
import { VOXEL_SCALE } from '../../../voxel/constants.js';
const SCATTER_SALT = 0x5ca77e5;
const STAMP_SALT = 0x57a3b01;
/** The single scatter root for a chunk request. */
export function scatterRoot(sink) {
    return {
        kind: 'scatter',
        id: hashInt2(sink.cx, sink.cz),
        seed: hashInt2(hashInt2(sink.cx, sink.cz), SCATTER_SALT),
        // The scatter covers whatever the request covers: its children decide what is actually near.
        aabb: sink.window,
        depth: 0,
    };
}
export function makeScatterKind(deps) {
    // Above this the scatter stops placing individual stamps and the per-column dot takes over. Read
    // once: it is a property of the stamp registry, which is static.
    const expandAtLod = Math.min(maxStampLod(), MAX_SCATTER_LOD);
    return {
        kind: 'scatter',
        maxLod: -1, // draws nothing of its own
        expandAtLod,
        emit: () => { },
        ySpan: () => null,
        children(_node, ctx, sink) {
            const placements = deps.placements(sink.cx, sink.cz, sink.lod);
            if (placements.length === 0)
                return [];
            const sorted = placements.slice().sort((a, b) => {
                const ab = isBuildingStamp(a.type) ? 1 : 0;
                const bb = isBuildingStamp(b.type) ? 1 : 0;
                return ab - bb; // stable: within a group, the scatter's own order
            });
            const out = [];
            for (const pl of sorted) {
                const aabb = stampFineBounds(pl, sink.lod, deps.heights, ctx.scale);
                if (!aabb)
                    continue; // no model for this type at this level
                out.push({
                    kind: 'stamp',
                    id: hashInt2(Math.floor(pl.worldX / VOXEL_SCALE), Math.floor(pl.worldZ / VOXEL_SCALE)),
                    seed: hashInt2(hashInt2(Math.floor(pl.worldX / VOXEL_SCALE), Math.floor(pl.worldZ / VOXEL_SCALE)), STAMP_SALT),
                    aabb,
                    depth: 1,
                    data: pl,
                });
            }
            return out;
        },
    };
}
export function makeStampKind(deps) {
    return {
        kind: 'stamp',
        maxLod: MAX_SCATTER_LOD,
        expandAtLod: -1, // a leaf — its own model is the whole picture
        children: () => [],
        emit(node, sink, ctx) {
            deps.placer().applyOne(sink.data, sink.cx, sink.cy, sink.cz, node.data, deps.heights, ctx.scale, sink.lod);
        },
        ySpan(node, _ctx, sink) {
            // Free: the box was measured when the node was made, and it is the same box the draw writes in.
            // The top is the model's last OCCUPIED row rather than the box's exclusive edge — the clip
            // counts rows, and one lattice cell of difference is one coarse chunk at the coarsest levels.
            return { bottom: node.aabb.minY, top: node.aabb.maxY - (1 << sink.lod) };
        },
    };
}
//# sourceMappingURL=scatter.js.map